import pages from '@configs/projects/pages';
import serverErrorsConstants from '@constants/serverErrors';
import snackbarTypes from '@constants/snackbarTypes';
import snackbarsConstants from '@constants/snackbars';
import useSnackbar from '@hooks/useSnackbar';
import { useAppDispatch } from '@hooks/useStore';
import { globalActions } from '@redux/slices/global';
import { projectsActions } from '@redux/slices/projects';
import * as Sentry from '@sentry/react';
import { IUsersValue } from '@typings/counter';
import { IProjectsListUpdateParams, IProjectsListRequestParams, ProjectsPageName } from '@typings/projects';
import { IRootSlice } from '@typings/rootSlice';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { userActions } from '../redux/slices/users/index';

const useProjects = () => {
    const dispatch = useAppDispatch();
    const projectsSlice = useSelector((state: IRootSlice) => state.projectsSlice);
    const userSlice = useSelector((state: IRootSlice) => state.userSlice);
    const { pathname } = useLocation();
    const { setSnackbar } = useSnackbar();
    const isDeleted = pathname.includes('deleted');

    const api = {
        /**
         * Инициализация
         */
        async init(): Promise<unknown> {
            await api.getProjectsTable({
                isUseRating: false,
            });
            api.getAccessRequestsList();
            return Promise.resolve();
        },

        getProjectsTable(updateParams: IProjectsListUpdateParams = {}): void {
            const deleted = String(isDeleted || false);
            const expanded = String(userSlice.isUserProjectsExpanded);
            const use_rating = String(updateParams.isUseRating || false);
            const search = updateParams.search || '';
            const offset = updateParams.offset || 0;
            const lastReqId = projectsSlice.tableLastReqId + 1;

            const newRequestParams: IProjectsListRequestParams = {
                deleted,
                expanded,
                use_rating,
                offset,
                search,
                lastReqId,
            };

            dispatch(projectsActions.updateRequestParams({ tableSearch: search }));

            dispatch(projectsActions.getProjectsTable(newRequestParams))
                .unwrap()
                .then((response) => {
                    if (response.responseStatus !== 200) return;

                    dispatch(
                        projectsActions.updateProjectsTable({
                            ...response,
                            offset,
                            search,
                            lastReqId,
                        }),
                    );
                })
                .catch((err) => {
                    Sentry.captureException(err);
                });
        },

        // Получаем список запросов на доступ по всем счетчикам
        getAccessRequestsList() {
            dispatch(userActions.getAccessRequestsList());
        },

        // Получаем список запросов на доступ к счетчику
        getAccessRequestsListByProject(projectId: number) {
            dispatch(userActions.getAccessRequestsListByProject(projectId));
        },

        getProjectsTableOffset(offset: number = 0): void {
            const search = projectsSlice.tableSearch;
            api.getProjectsTable({ offset, search });
        },

        applyModalPopup(projectId: number): Promise<void> {
            const pageName = api.getProjectsPageName();
            const configPage = pages[pageName];
            const counterActionName = configPage.counterActionName;
            const snackbarMessage = snackbarsConstants.counterMessages[configPage.snackbarMessage];

            dispatch(projectsActions[counterActionName](projectId)).then((res) => {
                if (res.error) return;

                if (res.payload.responseStatus === 200) {
                    dispatch(globalActions.setSnackbar(snackbarMessage));
                }
            });
            return Promise.resolve();
        },

        openModalPopup(modalPopupParams: Record<string, () => void> = {}): void {
            const pageName = api.getProjectsPageName();
            const modalPopupId = pages[pageName].modalPopupId;
            dispatch(globalActions.openModalPopup({ modalPopupId, modalPopupParams }));
        },

        getProjectsPageName: (): ProjectsPageName => (isDeleted ? 'deleted' : 'enabled'),

        editProjectUsers(id: number, usersList: IUsersValue[], successCallback: () => void): Promise<any> {
            const users = usersList.map((user) => ({
                email: user.email,
                access_level: user.accessLevel === 'edit' ? 0 : 1,
                can_edit_project: user.accessLevel === 'edit',
            }));

            const data = { users };

            return dispatch(projectsActions.save({ id, data })).then(({ payload }) => {
                if (payload.responseStatus === 200) {
                    successCallback?.();
                } else if (payload.errors && payload.errors.message) {
                    if (payload.errors.message === serverErrorsConstants.REQUEST_API_ERROR) {
                        setSnackbar('Произошла ошибка при запросе на сервер', snackbarTypes.ERROR);
                    } else if (payload.errors.message === serverErrorsConstants.RATE_LIMIT_EXCEEDED) {
                        setSnackbar(
                            'В день вы можете добавить не более 20 пользователей. Чтобы дать доступ большему числу людей, вернитесь завтра или обратитесь в <a href="mailto:counter@ads.sber.ru">службу поддержки.</a>',
                            snackbarTypes.WARNING,
                        );
                    }
                }
            });
        },
    };

    return { api };
};

export default useProjects;
